import { z } from 'zod';
import { LIMITS, PAPER_WIDTHS } from './constants';
import type { ReservationSlot } from './hours';
import type { ReceiptJob } from './receipt-data';
import type {
  AuditLogEntry,
  Branding,
  Domain,
  Location,
  LocationCard,
  Membership,
  OrderType,
  PaymentMethod,
  PaymentStatus,
  PrintJobKind,
  Profile,
  ReservationStatus,
  Restaurant,
  Role,
  StoreMenu,
  StoreProfile,
} from './types';

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const uuidSchema = z.string().uuid();

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

export const timezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, { message: 'Unknown timezone.' });

/** 'HH:MM' or 'HH:MM:SS' on a 24h clock. */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/);

export const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : undefined));

export const phoneSchema = z
  .string()
  .trim()
  .min(7)
  .max(LIMITS.maxPhoneLength)
  .regex(/^[0-9+()\-.\s]+$/, 'That phone number does not look right.');

export const emailSchema = z.string().trim().toLowerCase().email().max(LIMITS.maxEmailLength);

export const addressSchema = z.object({
  line1: z.string().trim().max(200).optional().default(''),
  line2: z.string().trim().max(200).optional().default(''),
  city: z.string().trim().max(100).optional().default(''),
  state: z.string().trim().max(100).optional().default(''),
  postal_code: z.string().trim().max(20).optional().default(''),
  country: z.string().trim().max(60).optional().default('US'),
});

export const brandingSchema = z.object({
  logo_key: z.string().trim().max(500).nullable().optional(),
  cover_key: z.string().trim().max(500).nullable().optional(),
  primary_color: z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
    .optional(),
  accent_color: z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
    .optional(),
  font: z.enum(['inter', 'dm-sans', 'space-grotesk', 'playfair', 'merriweather']).optional(),
});

/**
 * A customer contact. Orders/reservations require at least one way to reach the
 * customer (phone or email).
 */
export const customerContactSchema = z
  .object({
    name: z.string().trim().min(1).max(LIMITS.maxNameLength),
    phone: phoneSchema.optional(),
    email: emailSchema.optional(),
  })
  .strict()
  .refine((value) => Boolean(value.phone || value.email), {
    message: 'Add a phone number or email so the restaurant can reach you.',
    path: ['phone'],
  });

export const cartLineSchema = z
  .object({
    menu_item_id: uuidSchema,
    quantity: z.number().int().min(1).max(LIMITS.maxLineQuantity),
    modifier_option_ids: z.array(uuidSchema).max(50).default([]),
    notes: optionalText(200),
  })
  .strict();

export const turnstileTokenSchema = optionalText(4096);

// ---------------------------------------------------------------------------
// Storefront API — /api/store/*
// ---------------------------------------------------------------------------

export const STORE_API = {
  profile: '/api/store/profile',
  menu: '/api/store/menu',
  reservationSlots: '/api/store/reservation-slots',
  orders: '/api/store/orders',
  orderStatus: (token: string) => `/api/store/o/${token}`,
  reservations: '/api/store/reservations',
  reservationStatus: (token: string) => `/api/store/r/${token}`,
  reservationCancel: (token: string) => `/api/store/r/${token}/cancel`,
  marketplace: '/api/platform/marketplace',
} as const;

export const createOrderRequestSchema = z
  .object({
    items: z.array(cartLineSchema).min(1).max(100),
    customer: customerContactSchema,
    notes: optionalText(LIMITS.maxNotesLength),
    /** Required for pickup orders; ignored for table orders (server derives it). */
    payment_method: z.enum(['card', 'in_store']).optional(),
    /** ASAP only at launch; scheduled pickup comes later. */
    pickup_asap: z.literal(true).default(true),
    turnstile_token: turnstileTokenSchema,
    /** Present when the guest scanned a table QR code. */
    table_token: optionalText(128),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (!value.table_token && !value.payment_method) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payment_method'],
        message: 'Choose how you want to pay.',
      });
    }
  });

export type CreateOrderRequest = z.infer<typeof createOrderRequestSchema>;

export const createReservationRequestSchema = z
  .object({
    requested_for: z.string().datetime(),
    party_size: z.number().int().min(1).max(100),
    customer: customerContactSchema,
    notes: optionalText(LIMITS.maxNotesLength),
    turnstile_token: turnstileTokenSchema,
  })
  .strict();

export type CreateReservationRequest = z.infer<typeof createReservationRequestSchema>;

export const reservationSlotsQuerySchema = z.object({
  date: dateOnlySchema,
});

export interface ReservationSlotsResponse {
  timezone: string;
  date: string;
  slots: ReservationSlot[];
}

export interface CreateOrderResponse {
  order_token: string;
  order_number: number;
  status: string;
  payment_method: PaymentMethod;
  payment_status: PaymentStatus;
  total_cents: number;
  /** Present when payment_method = card: redirect the customer here. */
  checkout_url: string | null;
}

export interface OrderStatusItem {
  name: string;
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  modifiers: { group: string; option: string; price_delta_cents: number }[];
  notes: string | null;
}

export interface OrderStatusResponse {
  order_number: number;
  status: string;
  payment_method: PaymentMethod;
  payment_status: PaymentStatus;
  order_type: OrderType;
  table_label: string | null;
  /** Set when the restaurant configured a guest review link. */
  google_review_url: string | null;
  created_at: string;
  pickup_time: string | null;
  notes: string | null;
  items: OrderStatusItem[];
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  /** Masked PII, e.g. "Scott · •••-•••-4567". */
  customer: string;
  customer_email: string | null;
  location: {
    name: string;
    phone: string | null;
    address_lines: string[];
    timezone: string;
  };
}

export interface CreateReservationResponse {
  reservation_token: string;
  status: ReservationStatus;
  requested_for: string;
}

export interface ReservationStatusResponse {
  status: ReservationStatus;
  party_size: number;
  requested_for: string;
  notes: string | null;
  decline_reason: string | null;
  created_at: string;
  customer: string;
  can_cancel: boolean;
  location: {
    name: string;
    phone: string | null;
    address_lines: string[];
    timezone: string;
  };
}

export type StoreProfileResponse = StoreProfile;
export type StoreMenuResponse = StoreMenu;

// ---------------------------------------------------------------------------
// Platform API — /api/platform/* (Supabase JWT required except marketplace)
// ---------------------------------------------------------------------------

export const PLATFORM_API = {
  me: '/api/platform/me',
  register: '/api/platform/register',
  slugCheck: '/api/platform/slugs/check',
  uploads: '/api/platform/uploads',
  publish: (locationId: string) => `/api/platform/locations/${locationId}/publish`,
  unpublish: (locationId: string) => `/api/platform/locations/${locationId}/unpublish`,
  previewLink: (locationId: string) => `/api/platform/locations/${locationId}/preview-link`,
  changeSlug: (locationId: string) => `/api/platform/locations/${locationId}/slug`,
  domains: (locationId: string) => `/api/platform/locations/${locationId}/domains`,
  domainStatus: (domainId: string) => `/api/platform/domains/${domainId}`,
  domainDelete: (domainId: string) => `/api/platform/domains/${domainId}`,
  stripeConnect: '/api/platform/stripe/connect',
  stripeStatus: '/api/platform/stripe/status',
  pairingCode: (locationId: string) => `/api/platform/locations/${locationId}/pairing-code`,
  invites: '/api/platform/invites',
  inviteAccept: '/api/platform/invites/accept',
  refund: (orderId: string) => `/api/platform/orders/${orderId}/refund`,
  copyMenu: (locationId: string) => `/api/platform/locations/${locationId}/copy-menu`,
  marketplace: '/api/platform/marketplace',
} as const;

export const registerRequestSchema = z
  .object({
    restaurant_name: z.string().trim().min(1).max(120),
    restaurant_email: emailSchema.optional(),
    location: z.object({
      name: z.string().trim().min(1).max(120),
      slug: z.string().trim().toLowerCase().min(3).max(63),
      address: addressSchema.optional(),
      phone: phoneSchema.optional(),
      timezone: timezoneSchema,
      branding: brandingSchema.optional(),
    }),
  })
  .strict();

export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const domainRequestSchema = z
  .object({
    hostname: z
      .string()
      .trim()
      .toLowerCase()
      .min(4)
      .max(253)
      .regex(/^(?!-)[a-z0-9-]{1,63}(\.(?!-)[a-z0-9-]{1,63})+$/, 'That does not look like a domain.'),
  })
  .strict();

export type DomainRequest = z.infer<typeof domainRequestSchema>;

export const slugChangeRequestSchema = z
  .object({
    slug: z.string().trim().toLowerCase().min(3).max(63),
  })
  .strict();

export type SlugChangeRequest = z.infer<typeof slugChangeRequestSchema>;

export const inviteRequestSchema = z
  .object({
    email: emailSchema,
    role: z.enum(['owner', 'manager', 'staff']),
    location_ids: z.array(uuidSchema).max(50).nullable().default(null),
  })
  .strict();

export type InviteRequest = z.infer<typeof inviteRequestSchema>;

export const inviteAcceptRequestSchema = z.object({ token: z.string().trim().min(20).max(500) }).strict();

export const refundRequestSchema = z
  .object({
    amount_cents: z.number().int().min(1).optional(),
    reason: z.enum(['requested_by_customer', 'duplicate', 'fraudulent']).optional(),
  })
  .strict();

export type RefundRequest = z.infer<typeof refundRequestSchema>;

export const copyMenuRequestSchema = z
  .object({
    from_location_id: uuidSchema,
    categories: z.boolean().default(true),
    items: z.boolean().default(true),
    modifiers: z.boolean().default(true),
  })
  .strict();

export interface MembershipSummary {
  membership: Membership;
  restaurant: Restaurant;
  locations: Location[];
}

export interface PendingInvite {
  id: string;
  restaurant_name: string;
  role: Role;
  email: string;
  expires_at: string;
}

export interface MeResponse {
  profile: Profile | null;
  user: { id: string; email: string | null };
  memberships: MembershipSummary[];
  pending_invites: PendingInvite[];
}

export interface RegisterResponse {
  restaurant_id: string;
  location_id: string;
  slug: string;
}

export interface SlugCheckResponse {
  slug: string;
  available: boolean;
  reason?: string;
}

export interface UploadResponse {
  key: string;
  url: string;
  mime_type: string;
  bytes: number;
}

export interface PublishResponse {
  ok: true;
  storefront_url: string;
}

export interface PreviewLinkResponse {
  url: string;
  expires_at: string;
}

export interface DomainDnsInstructions {
  record_type: 'CNAME' | 'TXT';
  name: string;
  value: string;
  note: string;
}

export interface DomainResponse {
  domain: Domain;
  dns: DomainDnsInstructions[];
}

export interface StripeConnectResponse {
  url: string | null;
  charges_enabled: boolean;
  connected: boolean;
}

// ---------------------------------------------------------------------------
// Stripe Billing (adop's monthly subscription for a restaurant)
// ---------------------------------------------------------------------------

/** Plans that can be purchased. `free` is the no-subscription state. */
export const BILLING_PLAN_IDS = ['starter', 'growth', 'pro'] as const;
export type BillingPlanId = (typeof BILLING_PLAN_IDS)[number];

export interface BillingPlanOption {
  id: BillingPlanId;
  label: string;
  /** A Stripe price id is configured for this plan. */
  configured: boolean;
}

/** GET /api/platform/billing/status */
export interface BillingStatusResponse {
  /** All three price ids + the secret key are configured. */
  configured: boolean;
  /** Missing environment variable names when not configured (fail loudly). */
  missing: string[];
  plan: string;
  plan_status: string | null;
  plan_renews_at: string | null;
  has_customer: boolean;
  plans: BillingPlanOption[];
}

/** POST /api/platform/billing/checkout */
export const billingCheckoutRequestSchema = z
  .object({ plan: z.enum(BILLING_PLAN_IDS) })
  .strict();
export type BillingCheckoutRequest = z.infer<typeof billingCheckoutRequestSchema>;

export interface BillingCheckoutResponse {
  url: string;
}

/** POST /api/platform/billing/portal */
export interface BillingPortalResponse {
  url: string;
}

export interface PairingCodeResponse {
  code: string;
  expires_at: string;
  download_url: string;
}

export interface InviteResponse {
  invite_id: string;
  email: string;
  expires_at: string;
}

export interface InviteAcceptResponse {
  restaurant_id: string;
  role: Role;
}

export interface RefundResponse {
  ok: true;
  payment_status: PaymentStatus;
  amount_cents: number;
}

export interface MarketplaceResponse {
  locations: LocationCard[];
  total: number;
  page: number;
  page_size: number;
}

// ---------------------------------------------------------------------------
// Printer API — /api/printer/*
// ---------------------------------------------------------------------------

export const PRINTER_API = {
  pair: '/api/printer/pair',
  jobs: '/api/printer/jobs',
  ack: (jobId: string) => `/api/printer/jobs/${jobId}/ack`,
  heartbeat: '/api/printer/heartbeat',
} as const;

export const pairPrinterRequestSchema = z
  .object({
    code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your dashboard.'),
    device_name: z.string().trim().min(1).max(120),
    printer_name: z.string().trim().min(1).max(200),
    paper_width: z.union([z.literal(PAPER_WIDTHS[0]), z.literal(PAPER_WIDTHS[1])]).default(80),
    app_version: optionalText(40),
  })
  .strict();

export type PairPrinterRequest = z.infer<typeof pairPrinterRequestSchema>;

export interface PairPrinterResponse {
  device_token: string;
  device_id: string;
  location: { id: string; name: string; timezone: string };
  restaurant_name: string;
}

export const ackJobRequestSchema = z
  .object({
    ok: z.boolean(),
    error: optionalText(500),
  })
  .strict();

export type AckJobRequest = z.infer<typeof ackJobRequestSchema>;

export const heartbeatRequestSchema = z
  .object({
    app_version: optionalText(40),
    printer_status: z.enum(['ready', 'offline', 'error', 'unknown']).default('unknown'),
    printer_name: optionalText(200),
  })
  .strict();

export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;

export interface HeartbeatResponse {
  ok: true;
  location_id: string;
  server_time: string;
}

export type JobKind = PrintJobKind;

export interface JobsResponseEnvelope {
  jobs: ReceiptJob[];
}

// ---------------------------------------------------------------------------
// Dashboard helpers (client-side derived types)
// ---------------------------------------------------------------------------

export interface DashboardOrderRow {
  order: {
    id: string;
    order_number: number;
    status: string;
    payment_method: PaymentMethod;
    payment_status: PaymentStatus;
    order_type: OrderType;
    table_label: string | null;
    total_cents: number;
    customer_name: string;
    created_at: string;
  };
  item_count: number;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
}

export interface BrandingUpdate {
  branding: Branding;
}
